/** Wallet pass slot → attendee/event field mapping (mirrors badge {{token}} pattern). */

export const WALLET_PASS_SLOTS = [
  { id: "header", role: "Header", defaultLabel: "EVENT", fixedSource: "event.title" },
  { id: "primary", role: "Primary", defaultLabel: "ATTENDEE", defaultSource: "attendee.name" },
  { id: "secondary1", role: "Secondary 1", defaultLabel: "TICKET", defaultSource: "attendee.ticket" },
  { id: "secondary2", role: "Secondary 2", defaultLabel: "GATE", defaultSource: "static", defaultStatic: "Gate A" },
  { id: "auxiliary", role: "Auxiliary", defaultLabel: "REF", defaultSource: "attendee.id" },
  { id: "qr", role: "Barcode", defaultLabel: "QR", defaultSource: "qr.code" },
];

const EVENT_SOURCES = [
  { value: "event.title", label: "Event · title" },
  { value: "event.venue", label: "Event · venue" },
  { value: "event.startDate", label: "Event · start date" },
  { value: "event.endDate", label: "Event · end date" },
];

const ATTENDEE_LABELS = {
  name: "Attendee · name",
  email: "Attendee · email",
  company: "Attendee · company",
  role: "Attendee · role",
  ticket: "Attendee · ticket",
  id: "Attendee · id",
  status: "Attendee · status",
  country: "Attendee · country",
  mobile: "Attendee · mobile",
};

export function defaultFieldMappings(gateLabel = "Gate A") {
  const out = {};
  for (const slot of WALLET_PASS_SLOTS) {
    if (slot.fixedSource) {
      out[slot.id] = { label: slot.defaultLabel, source: slot.fixedSource };
      continue;
    }
    out[slot.id] = {
      label: slot.defaultLabel,
      source: slot.defaultSource,
      ...(slot.defaultSource === "static" ? { staticValue: gateLabel || slot.defaultStatic || "" } : {}),
    };
  }
  return out;
}

/** Migrate legacy wallet state that stored literal preview strings. */
export function normalizeWalletPassConfig(value, eventTitle = "Event") {
  const base = value && typeof value === "object" ? { ...value } : {};
  const gate = String(base.gateLabel || "Gate A").trim() || "Gate A";

  if (base.fieldMappings && typeof base.fieldMappings === "object") {
    return {
      ...base,
      fieldMappings: { ...defaultFieldMappings(gate), ...base.fieldMappings },
      previewAttendeeId: base.previewAttendeeId || "",
    };
  }

  const mappings = defaultFieldMappings(gate);
  if (base.holder && base.holder !== "Attendee Name") {
    mappings.primary = { ...mappings.primary, source: "static", staticValue: base.holder };
  }
  if (base.tier && base.tier !== "VIP") {
    mappings.secondary1 = { ...mappings.secondary1, source: "static", staticValue: base.tier };
  }
  if (base.seat && !String(base.seat).startsWith("TICKET-")) {
    mappings.auxiliary = { ...mappings.auxiliary, source: "static", staticValue: base.seat };
  }
  mappings.header = { label: "EVENT", source: "event.title" };
  mappings.secondary2 = { label: "GATE", source: "static", staticValue: gate };

  return {
    ...base,
    fieldMappings: mappings,
    previewAttendeeId: base.previewAttendeeId || "",
  };
}

export function buildSourceOptions(dataSources, customFields = []) {
  const attendeeFields = Array.isArray(dataSources?.attendee?.fields)
    ? dataSources.attendee.fields
    : Object.keys(ATTENDEE_LABELS);

  const attendeeOpts = attendeeFields.map((field) => ({
    value: `attendee.${field}`,
    label: ATTENDEE_LABELS[field] || `Attendee · ${field}`,
    group: "Attendee list",
  }));

  const customOpts = (customFields || []).map((f) => ({
    value: `custom.${f.id}`,
    label: `Form · ${f.label || f.id}`,
    group: "Registration form",
  }));

  return [
    ...EVENT_SOURCES.map((o) => ({ ...o, group: "Event" })),
    ...attendeeOpts,
    ...customOpts,
    { value: "static", label: "Fixed text (same for every pass)", group: "Other" },
    { value: "qr.code", label: "Badge QR payload (B1|…)", group: "Other" },
  ];
}

function readPath(obj, path) {
  if (!obj || !path) return "";
  return String(obj[path] ?? "").trim();
}

export function resolvePassFields(fieldMappings, context) {
  const { event = {}, attendee = {}, customAnswers = {}, qrCode = "" } = context;
  const resolved = {};

  for (const slot of WALLET_PASS_SLOTS) {
    const mapping = fieldMappings?.[slot.id] || {};
    const label = String(mapping.label || slot.defaultLabel).trim() || slot.defaultLabel;
    const source = slot.fixedSource || mapping.source || slot.defaultSource;
    let value = "";

    if (source === "static") {
      value = String(mapping.staticValue ?? "").trim();
    } else if (source === "qr.code") {
      value = qrCode || "";
    } else if (source.startsWith("attendee.")) {
      value = readPath(attendee, source.slice("attendee.".length));
    } else if (source.startsWith("event.")) {
      value = readPath(event, source.slice("event.".length));
    } else if (source.startsWith("custom.")) {
      const fieldId = source.slice("custom.".length);
      const raw = customAnswers[fieldId];
      value = raw == null ? "" : String(Array.isArray(raw) ? raw.join(", ") : raw).trim();
    }

    if (!value && source === "static") {
      value = String(mapping.staticValue ?? slot.defaultStatic ?? "").trim();
    }

    resolved[slot.id] = { label, value: value || "—", source };
  }

  if (event?.title && resolved.header?.value === "—") {
    resolved.header = { ...resolved.header, value: event.title };
  }

  return resolved;
}

export function buildQrPayload(eventId, attendeeId) {
  if (!eventId || !attendeeId) return "";
  return `B1|${eventId}|attendee|${attendeeId}`;
}
